import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigationIcon from '@mui/icons-material/Navigation';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099DAInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-DA instructions for Tax Year 2025"
          description="Learn how to complete IRS Form 1099-DA for Tax Year 2025 with TaxZerone’s step-by-step instructions to report distributions accurately and on time."
          keywords=""
          canonical="resources/information-returns/form-1099-da-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-DA</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file your IRS Form 1099-DA for the 2025 tax year with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f1099da`}
            // href="/contact-us/"
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-da-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-DA at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-DA{' '}
                        </strong>{' '}
                        is used to report digital asset transactions, including
                        sales, exchanges, and certain other dispositions of
                        digital assets such as cryptocurrency.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>,
                        and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>{' '}
                        if filing on paper or
                        <strong style={{ fontWeight: 600 }}>March 31</strong> if
                        filing electronically.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions including
                        broker and recipient information, Account Number,
                        Digital Asset Identifier,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong> (Date
                        and Time of Transaction),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>{' '}
                        (Quantity of Digital Assets),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 3</strong>{' '}
                        (Gross Proceeds),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 4</strong>{' '}
                        (Federal Income Tax Withheld),{' '}
                        <strong style={{ fontWeight: 600 }}> Box 5</strong>{' '}
                        (Cost Basis), Box 6 (Gain or Loss Information),
                        Transaction Codes, and State Tax Reporting Information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        Late or incorrect filing may result in IRS penalties
                        ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Form 1099-DA,{' '}
                  <span style={{ fontWeight: 600 }}>
                    Digital Asset Proceeds From Broker Transactions
                  </span>
                  , is a new IRS information return introduced to report sales
                  and exchanges of digital assets such as cryptocurrencies,
                  stablecoins, tokenized securities, and certain NFTs. Brokers
                  and digital asset middlemen must file this form with the IRS
                  and furnish copies to customers when they facilitate digital
                  asset transactions.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Starting with the{' '}
                  <span style={{ fontWeight: 600 }}>2025 tax year</span>,
                  brokers must report{' '}
                  <span style={{ fontWeight: 600 }}>gross proceeds</span> from
                  sales. Beginning in{' '}
                  <span style={{ fontWeight: 600 }}>2026</span>, basis reporting
                  for covered securities will also be required.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  At <span style={{ fontWeight: 600 }}>TaxZerone</span>, we make{' '}
                  IRS Form 1099-DA e-filing simple, secure, and
                  IRS-compliant.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 1099-DA?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1099-DA must be filed by U.S. digital asset brokers who
                  handle the sale, exchange, or disposition of digital assets on
                  behalf of their customers. If you’re in the business of
                  facilitating digital asset transactions, the IRS requires you
                  to report them.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Exchanges and trading platforms
                    </span>{' '}
                    that execute sales of digital assets such as cryptocurrency,
                    stablecoins, or NFTs.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Businesses</span> that
                    redeem digital assets they issued or created.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Intermediaries</span>{' '}
                    such as kiosks, payment processors for digital assets, and
                    real estate reporting persons who accept digital assets.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Custodians or trustees
                    </span>{' '}
                    selling digital assets on behalf of clients.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      U.S. branches and U.S. persons acting as brokers
                    </span>{' '}
                    (foreign branches are generally excluded).
                  </li>
                </Box>

                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={false}
                >
                  <b>Exceptions:</b> Mining or staking validators and
                  hardware/software providers that only supply wallets are not
                  considered brokers.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 25, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-DA Filing Deadlines (Tax Year 2025, Filed in 2026)
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Brokers and intermediaries dealing with digital assets must
                  meet strict filing deadlines for
                  <br /> 1099-DA Form:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Filing Requirement
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Deadline
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Furnish Copy B to Recipients </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            February 17, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>File with IRS (Paper Filing) </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            March 2, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>File with IRS (Electronic)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            March 31, 2026{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#caf0f8',
                    mb: 2,
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {' '}
                    <WarningAmberIcon
                      sx={{ mb: -0.5, color: '#bc6c25' }}
                    />{' '}
                    <span style={{ fontWeight: 600 }}> IRS Requirement:</span>{' '}
                    If you are filing{' '}
                    <span style={{ fontWeight: 600 }}>
                      10 or more information returns
                    </span>{' '}
                    (combined across all 1099s, W-2s, etc.), you are{' '}
                    <span style={{ fontWeight: 600 }}>required to e-file</span>.
                  </Typography>
                </Box>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  icon={false}
                >
                  <b>*Note:</b> Since February 15, 2026, falls on a Sunday and
                  February 16, 2026, is a federal holiday (Presidents’ Day), the
                  due date is moved to Tuesday, February 17, 2026.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for Late or Incorrect Filing
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    If a form is filed{' '}
                    <span style={{ fontWeight: 600 }}>within 30 days late</span>
                    , the penalty is{' '}
                    <span style={{ fontWeight: 600 }}>$60 per form</span>.{' '}
                  </li>
                  <li>
                    If a form is filed{' '}
                    <span style={{ fontWeight: 600 }}>
                      more than 30 days late but by August 1
                    </span>
                    , the penalty is{' '}
                    <span style={{ fontWeight: 600 }}>$130 per form</span>.{' '}
                  </li>
                  <li>
                    If a form is filed{' '}
                    <span style={{ fontWeight: 600 }}>
                      after August 1 or not filed at all
                    </span>
                    , the penalty is{' '}
                    <span style={{ fontWeight: 600 }}>$340 per form</span>.{' '}
                  </li>
                  <li>
                    If a form is filed with{' '}
                    <span style={{ fontWeight: 600 }}>
                      intentional disregard
                    </span>
                    , the penalty is{' '}
                    <span style={{ fontWeight: 600 }}>$680 per form</span>, with{' '}
                    <span style={{ fontWeight: 600 }}>no upper limit</span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The maximum penalties are different for small and large firms,
                  as well as for government agencies. There is no limit for
                  willful disrespect.{' '}
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesIcon />}
                >
                  <b>TaxZerone Tip:</b> E-filing your 1099 da early and getting
                  fast IRS confirmation will help you avoid these expensive
                  fines.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to Fill Out Form 1099-DA Instructions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before we get into the step-by-step filing steps, you should
                  know that Form 1099-DA comes with up to four copies, each with
                  a different use:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Copy A:</span> Sent to the
                    Internal Revenue Service (IRS).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Copy 1:</span> Submitted
                    to the state tax department (if required).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Copy B:</span> Given to
                    the payment recipient for their records and tax filing.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Copy 2:</span> Attached to
                    the recipient’s state income tax return (if
                    applicable).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Let&apos;s go over the most important filing instructions for
                  each part of the form with that in mind:{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filer’s Details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/1099da-filer-img.webp" />
                  <source srcSet="/assets/images/1099da-filer-img.png" />
                  <img
                    src="/assets/images/1099da-filer-img.png"
                    loading="lazy"
                    alt="Filer’s Details"
                    width={410}
                    height={212}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Enter the{' '}
                      <span style={{ fontWeight: 600 }}>
                        filer’s name, address, and TIN (EIN if a business;
                        SSN/ITIN if an individual broker)
                      </span>
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient’s Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-recipient-img.webp" />
                  <source srcSet="/assets/images/1099da-recipient-img.png" />
                  <img
                    src="/assets/images/1099da-recipient-img.png"
                    loading="lazy"
                    alt="Recipient’s Details "
                    width={410}
                    height={312}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Enter the{' '}
                      <span style={{ fontWeight: 600 }}>recipient’s TIN</span>,
                      legal name, and complete mailing address (street, city,
                      state, ZIP, or foreign postal code).
                    </Box>
                  </li>

                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Add the{' '}
                      <span style={{ fontWeight: 600 }}>account number</span> if
                      the recipient has multiple accounts with you.
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  CUSIP Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-cusip-img.webp" />
                  <source srcSet="/assets/images/1099da-cusip-img.png" />
                  <img
                    src="/assets/images/1099da-cusip-img.png"
                    loading="lazy"
                    alt="CUSIP Number"
                    width={410}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    For brokers reporting a tokenized security under Regulations
                    §1.6045-1(c)(8)(i)(D)(1), enter the CUSIP or other
                    applicable identifying number of the digital asset.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Applicable Checkbox on Form 8949:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-application-img.webp" />
                  <source srcSet="/assets/images/1099da-application-img.png" />
                  <img
                    src="/assets/images/1099da-application-img.png"
                    loading="lazy"
                    alt="Applicable Checkbox on Form 8949: "
                    width={217}
                    height={82}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Use this box to enter a one-letter code to help the
                      recipient report the transaction on{' '}
                      <Link
                        href="https://www.irs.gov/forms-pubs/about-form-8949"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 8949
                      </Link>{' '}
                      and/or{' '}
                      <Link
                        href="https://www.irs.gov/forms-pubs/about-schedule-d-form-1040"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        Schedule D (Form 1040)
                      </Link>
                      :
                    </Box>
                  </li>
                </Box>
                <ul style={{ marginBottom: 30, marginLeft: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Code G.</span> This code
                    indicates a short-term transaction for which the cost or
                    other basis is being reported to the IRS.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Code H.</span> This code
                    indicates a short-term transaction for which the cost or
                    other basis is not being reported to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Code J.</span> This code
                    indicates a long-term transaction for which the cost or
                    other basis is being reported to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Code K.</span> This code
                    indicates a long-term transaction for which the cost or
                    other basis is not being reported to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Code Y.</span> Use this
                    code to report a transaction if you cannot determine whether
                    the recipient should check box H or box K on Form 8949
                    because the holding period is unknown.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1a – Code for Digital Asset
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-digital-assets-img.webp" />
                  <source srcSet="/assets/images/1099da-digital-assets-img.png" />
                  <img
                    src="/assets/images/1099da-digital-assets-img.png"
                    loading="lazy"
                    alt="Box 1a – Code for Digital Asset"
                    width={338}
                    height={61}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Enter the{' '}
                      <span style={{ fontWeight: 600 }}>
                        nine-character
                      </span>{' '}
                      alphanumeric digital token identifier assigned by the
                      Digital Token Identifier Foundation (DTIF). You can look
                      up your assets in the{' '}
                      <Link
                        href="https://dtif.org/registry-search"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        DTIF Registry
                      </Link>
                      . If the asset is not registered with DTIF, enter{' '}
                      <span style={{ fontWeight: 600 }}>“999999999.”</span>
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1b – Name of Digital Asset
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-1b-assets-img.webp" />
                  <source srcSet="/assets/images/1099da-1b-assets-img.png" />
                  <img
                    src="/assets/images/1099da-1b-assets-img.png"
                    loading="lazy"
                    alt="Box 1b – Name of Digital Asset "
                    width={337}
                    height={72}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter the full name of the digital asset being reported. If
                    a DTIF identifier was entered in Box 1a, provide the name
                    matching the DTIF registration. For qualifying stablecoins,
                    enter the stablecoin’s name. For specified NFTs, enter
                    “Specified NFTs.” s
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1c – Number of Units
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-1c-number-img.webp" />
                  <source srcSet="/assets/images/1099da-1c-number-img.png" />
                  <img
                    src="/assets/images/1099da-1c-number-img.png"
                    loading="lazy"
                    alt="Box 1c – Number of Units"
                    width={337}
                    height={41}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter the number of digital asset units sold, exchanged, or
                    otherwise disposed of, up to 18 decimal places. For
                    qualifying stablecoins, enter the total stablecoin units.
                    For specified NFTs, enter the total number of NFT units.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1d – Date Acquired
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-1d-acquired-img.webp" />
                  <source srcSet="/assets/images/1099da-1d-acquired-img.png" />
                  <img
                    src="/assets/images/1099da-1d-acquired-img.png"
                    loading="lazy"
                    alt="Box 1d – Date Acquired"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter the date you acquired the digital asset in&nbsp;
                    <span style={{ fontWeight: 600 }}>MM/DD/YYYY</span>
                    &nbsp;format (e.g., 05/24/2025).
                  </li>
                </Box>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <span style={{ fontWeight: 600 }}>Leave blank if</span> the
                    assets were acquired on multiple dates, if Box 9 is checked
                    and the acquisition date is unknown, or when reporting
                    qualifying stablecoins or specified NFTs using an optional
                    reporting method.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1e – Date Sold or disposed
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-1e-datesold-img.webp" />
                  <source srcSet="/assets/images/1099da-1e-datesold-img.png" />
                  <img
                    src="/assets/images/1099da-1e-datesold-img.png"
                    loading="lazy"
                    alt="Box 1e – Date Sold or disposed"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Enter the sale date of the digital asset in{' '}
                      <span style={{ fontWeight: 600 }}>MM/DD/YYYY</span>{' '}
                      format.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1f – Proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-proceeds-img.webp" />
                  <source srcSet="/assets/images/1099da-proceeds-img.png" />
                  <img
                    src="/assets/images/1099da-proceeds-img.png"
                    loading="lazy"
                    alt="Box 1f – Proceeds"
                    width={170}
                    height={41}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Report gross proceeds from the sale (cash, property,
                    services, or other digital assets), reduced by transaction
                    fees.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1g – Cost or Other Basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-1g-costbasis-img.webp" />
                  <source srcSet="/assets/images/1099da-1g-costbasis-img.png" />
                  <img
                    src="/assets/images/1099da-1g-costbasis-img.png"
                    loading="lazy"
                    alt="Box 1g – Cost or Other Basis"
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter the adjusted basis of the digital asset sold. If the
                    asset is not a covered security and you check Box 9 without
                    reporting basis, leave Box 1g blank. Enter “0” only if the
                    asset’s basis is zero.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1h – Accrued Market Discount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-1h-accurued-img.webp" />
                  <source srcSet="/assets/images/1099-1h-accurued-img.png" />
                  <img
                    src="/assets/images/1099-1h-accurued-img.png"
                    loading="lazy"
                    alt="Box 1h – Accrued Market Discount "
                    width={170}
                    height={41}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter the accrued market discount amount for digital assets
                    that are both covered securities and debt instruments for
                    federal income tax purposes.
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 1i – Wash Sale Loss Disallowed
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-1i-washsale-img.webp" />
                  <source srcSet="/assets/images/1099da-1i-washsale-img.png" />
                  <img
                    src="/assets/images/1099da-1i-washsale-img.png"
                    loading="lazy"
                    alt="Box 1i – Wash Sale Loss Disallowed"
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter disallowed losses under wash sale rules (applies
                    mainly to tokenized securities).
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 2 – Check if Basis Reported to IRS
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-basis-report-img.webp" />
                  <source srcSet="/assets/images/1099da-basis-report-img.png" />
                  <img
                    src="/assets/images/1099da-basis-report-img.png"
                    loading="lazy"
                    alt="Box 2 – Check if Basis Reported to IRS "
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Check this box if you are either not checking Box 9 or are
                    checking Box 9 but still reporting basis in Box 1g. Do not
                    check this box for sales of qualifying stablecoins or
                    specified NFTs under an optional reporting method.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 3a – Reported to IRS:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-3a-net-proceeds-img.webp" />
                  <source srcSet="/assets/images/1099da-3a-net-proceeds-img.png" />
                  <img
                    src="/assets/images/1099da-3a-net-proceeds-img.png"
                    loading="lazy"
                    alt="Box 3a – Reported to IRS:"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Indicate whether proceeds are gross or net of premiums/fees.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 3b – Check if Proceeds Are Form a Qualified Opportunity
                  Fund (QOF)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-3b-qof-img.webp" />
                  <source srcSet="/assets/images/1099da-3b-qof-img.png" />
                  <img
                    src="/assets/images/1099da-3b-qof-img.png"
                    loading="lazy"
                    alt="Box 3b – QOF "
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Check this box if the sale involves a disposition of an
                    interest in a Qualified Opportunity Fund.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 4 – Federal Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-federal-tax-img.webp" />
                  <source srcSet="/assets/images/1099da-federal-tax-img.png" />
                  <img
                    src="/assets/images/1099da-federal-tax-img.png"
                    loading="lazy"
                    alt="Box 4 – Federal Income Tax Withheld"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Report any backup withholding.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 5 – Check if Loss Is Not Allowed based on amount in 1f
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-check-loss-img.webp" />
                  <source srcSet="/assets/images/1099da-check-loss-img.png" />
                  <img
                    src="/assets/images/1099da-check-loss-img.png"
                    loading="lazy"
                    alt="Box 5 – Check if Loss Is Not Allowed"
                    width={134}
                    height={71}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Check this box if the loss reported in Box 1f is not
                    allowed.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 6 – Gain or Loss
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-gain-loss-img.webp" />
                  <source srcSet="/assets/images/1099da-gain-loss-img.png" />
                  <img
                    src="/assets/images/1099da-gain-loss-img.png"
                    loading="lazy"
                    alt="Box 6 – Gain or Loss"
                    width={278}
                    height={72}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Report the profit or loss from digital assets that were{' '}
                      <span style={{ fontWeight: 600 }}>
                        sold, exchanged, or otherwise disposed of
                      </span>
                      . Indicate if it was short-term or long-term and if any
                      part was ordinary income.
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 7 – Check if Only Cash Proceeds Were Paid in the
                  Transaction
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-only-cash-img.webp" />
                  <source srcSet="/assets/images/1099da-only-cash-img.png" />
                  <img
                    src="/assets/images/1099da-only-cash-img.png"
                    loading="lazy"
                    alt="Box 7 – Only Cash Proceeds"
                    width={170}
                    height={72}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Check this box if only cash (U.S. dollars or any convertible
                    foreign currency issued by a government or central bank) was
                    paid in the transaction. Check this box even if you combined
                    the reporting of the underlying transaction with the report
                    of the transaction used to pay broker fees.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 8 – Check if broker reflied on customer-provided
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-customer-img.webp" />
                  <source srcSet="/assets/images/1099da-customer-img.png" />
                  <img
                    src="/assets/images/1099da-customer-img.png"
                    loading="lazy"
                    alt="Box 8 – Customer-Provided Acquisition Info"
                    width={170}
                    height={72}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Check if you{' '}
                      <span style={{ fontWeight: 600 }}>received</span>{' '}
                      customer-provided acquisition info to identify digital
                      assets{' '}
                      <span style={{ fontWeight: 600 }}>
                        sold, exchanged, or disposed of
                      </span>
                      , even if not used for the lot reported. Skip if all such
                      assets were already disposed of.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 9 – Check id digital asset is a Noncovered Security
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-noncovered-img.webp" />
                  <source srcSet="/assets/images/1099da-noncovered-img.png" />
                  <img
                    src="/assets/images/1099da-noncovered-img.png"
                    loading="lazy"
                    alt="Box 9 – Noncovered Security"
                    width={134}
                    height={92}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Check this box if reporting a{' '}
                      <span style={{ fontWeight: 600 }}>
                        sale of a noncovered security
                      </span>
                      . Do not check for covered securities or qualifying
                      stablecoins/NFTs using the optional reporting method. If
                      checked, you did not have to complete boxes 1d, 1g, 1h,
                      1i, and 6, or check box 2. Completing them voluntarily
                      does not incur penalties under sections 6721 and 6722.
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 11a–11c – Reason Digital Asset Was Eligible for an
                  Optional Reporting Method
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <picture>
                    <source srcSet="/assets/images/1099da-11a-eligible-img.webp" />
                    <source srcSet="/assets/images/1099da-11a-eligible-img.png" />
                    <img
                      src="/assets/images/1099da-11a-eligible-img.png"
                      loading="lazy"
                      alt="Box 11a–11c – Reason Digital Asset Was Eligible for an Optional Reporting Method"
                      width={170}
                      height={92}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <picture>
                  <source srcSet="/assets/images/1099da-11b-reason-img.webp" />
                  <source srcSet="/assets/images/1099da-11b-reason-img.png" />
                  <img
                    src="/assets/images/1099da-11b-reason-img.png"
                    loading="lazy"
                    alt="Box 11a–11c – Reason Digital Asset Was Eligible for an Optional Reporting Method"
                    width={362}
                    height={82}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      If you <span style={{ fontWeight: 600 }}>
                        reported
                      </span>{' '}
                      sales of a digital asset eligible for an optional
                      reporting method, check the applicable box to indicate the
                      type of digital asset.
                    </Box>
                  </li>
                </Box>
                <ul style={{ marginBottom: 30, marginLeft: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    If you checked{' '}
                    <span style={{ fontWeight: 600 }}>
                      “Qualifying stablecoins,”
                    </span>{' '}
                    you must complete{' '}
                    <span style={{ fontWeight: 600 }}>box 11b</span>.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If you checked{' '}
                    <span style={{ fontWeight: 600 }}>“Specified NFTs,”</span>{' '}
                    you must complete box 11b and, if applicable,{' '}
                    <span style={{ fontWeight: 600 }}>box 11c</span>.
                  </li>
                  <li>
                    If neither box was checked, you did not have to complete
                    <span style={{ fontWeight: 600 }}>boxes 11b or 11c</span>
                    .{' '}
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12a – Number of Units Transferred In
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-transferred-img.webp" />
                  <source srcSet="/assets/images/1099da-transferred-img.png" />
                  <img
                    src="/assets/images/1099da-transferred-img.png"
                    loading="lazy"
                    alt="Box 12a – Number of Units Transferred In"
                    width={218}
                    height={82}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Report the number of digital asset units that were{' '}
                      <span style={{ fontWeight: 600 }}>
                        transferred into a custodial account
                      </span>
                      . Enter the amount to{' '}
                      <span style={{ fontWeight: 600 }}>18 decimal places</span>
                      . If you reported sales of{' '}
                      <span style={{ fontWeight: 600 }}>
                        qualifying stablecoins
                      </span>{' '}
                      or
                      <span style={{ fontWeight: 600 }}>
                        specified NFTs
                      </span>{' '}
                      using an optional reporting method, leave this box (12a)
                      blank.
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12b – Transfer-In Date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-12b-transfer-img.webp" />
                  <source srcSet="/assets/images/1099da-12b-transfer-img.png" />
                  <img
                    src="/assets/images/1099da-12b-transfer-img.png"
                    loading="lazy"
                    alt="Box 12b – Transfer-In Date"
                    width={170}
                    height={81}
                    className="proportional-img"
                  />
                </picture>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      Enter the date the digital assets were{' '}
                      <span style={{ fontWeight: 600 }}>
                        transferred into a custodial account
                      </span>
                      , using the{' '}
                      <span style={{ fontWeight: 600 }}>MM/DD/YYYY</span>{' '}
                      format.
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 14–16 – State Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099da-state-information-img.webp" />
                  <source srcSet="/assets/images/1099da-state-information-img.png" />
                  <img
                    src="/assets/images/1099da-state-information-img.png"
                    loading="lazy"
                    alt="Boxes 14–16 – State Information "
                    width={748}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    Enter the state boxes&nbsp;
                    <span style={{ fontWeight: 600 }}>
                      only if the amounts are required to be reported to the
                      states:
                    </span>
                  </li>
                </Box>
                <ul style={{ marginBottom: 30, marginLeft: 20 }}>
                  <li style={{ marginBottom: 15 }}>
                    <span style={{ fontWeight: 600 }}>Box 14:</span> Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      abbreviated name of the state
                    </span>
                    .{' '}
                  </li>

                  <li style={{ marginBottom: 15 }}>
                    <span style={{ fontWeight: 600 }}>Box 15:</span> Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      filer’s state identification number
                    </span>{' '}
                    assigned by the state.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Box 16:</span> Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      amount of state income tax withheld
                    </span>
                    , if any.{' '}
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These boxes are for your convenience and are{' '}
                  <span style={{ fontWeight: 600 }}>
                    not required for IRS filing
                  </span>
                  . If reporting for more than one state, use the dash line to
                  separate the information. If a state requires a paper copy,
                  provide <span style={{ fontWeight: 600 }}>Copy 1</span> to the
                  state and give <span style={{ fontWeight: 600 }}>Copy 2</span>{' '}
                  to the payee for their state income tax return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-DA with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-da/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1099 DA
                  </Link>{' '}
                  with TaxZerone is simple, fast, and secure. Before you start,
                  make sure you have all the required information ready to avoid
                  mistakes and save time.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  What You Need to eFile 1099-DA Form
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Filer information:</span>{' '}
                    Name, address, and TIN (EIN for businesses or SSN/ITIN for
                    individuals).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Recipient information:
                    </span>{' '}
                    Name, address, TIN, and account number (if applicable).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Transaction details:
                    </span>{' '}
                    Digital asset information, proceeds, cost/basis, gain/loss,
                    and Form 8949 code (G, H, or J).{' '}
                  </li>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Fill Out Form 1099-DA
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter all required details, including your information and the
                  recipient’s details, the digital asset transaction amounts,
                  and the applicable Form 8949 code.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Review and Submit
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Carefully check all entries. TaxZerone automatically validates
                  your form to catch errors or missing information. Once
                  verified,{' '}
                  <span style={{ fontWeight: 600 }}>
                    e-file the form directly with the IRS
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Provide the Recipient Copy
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  After submission, share a copy with the recipient for their
                  tax records. You can send it securely via email or provide
                  access through the{' '}
                  <span style={{ fontWeight: 600 }}>ZeroneVault portal</span>,
                  where recipients can view their forms anytime.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-DA with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-da/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-file Form 1099-DA
                  </Link>{' '}
                  using TaxZerone and enjoy multiple benefits. Here are some of
                  the key advantages:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Built-in IRS Checks:
                    </span>{' '}
                    Lowers the possibility of rejections or fines by
                    automatically verifying your Form 1099-DA entries to
                    identify any missing or inaccurate information.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Share Recipient Copies Easily:
                    </span>{' '}
                    Use <span style={{ fontWeight: 600 }}>ZeroneVault</span> to
                    send recipient copies digitally or opt for postal
                    delivery—no manual mailing is necessary.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Quick Bulk Filing:</span>{' '}
                    To expedite the reporting process, upload hundreds or even
                    thousands of 1099-DA documents at once.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Affordable Pricing:
                    </span>{' '}
                    Time and money can be saved without sacrificing accuracy
                    with competitive pricing based on file volume.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Step-by-Step Filing Interface:
                    </span>{' '}
                    No technical knowledge or tax experience is required; an
                    intuitive tool walks you through each step.{' '}
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more details, refer to the{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099da"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS Instructions for Form 1099-DA
                        </Link>{' '}
                        and the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/f1099da.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1099-DA PDF
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 1099 DA with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Start e-filing your Form 1099 DA at just{' '}
              <span style={{ fontWeight: 600 }}>$2.49 per form</span>, with bulk
              filing <br />
              rates available for as low as{' '}
              <span style={{ fontWeight: 600 }}>$0.59 per form</span>.
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099da`}
                // href="/contact-us/"
              >
                E-file Now
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099DAInstructions;
